import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Toaster as Sonner } from 'sonner';
import { Theme } from '@radix-ui/themes';
import { useTheme } from '@/hooks/useTheme';

type ToasterProps = React.ComponentProps<typeof Sonner>;

const Toaster = ({ ...props }: ToasterProps) => {
  const { mounted } = useTheme();
  const [documentTheme, setDocumentTheme] = useState<'light' | 'dark'>('light');

  // Determine theme based on actual document class, not resolvedTheme
  // This ensures toast theme always matches the actual site theme
  useEffect(() => {
    if (!mounted) return;

    const updateTheme = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setDocumentTheme(isDark ? 'dark' : 'light');
    };

    // Initial check
    updateTheme();

    // Watch for class changes on document element
    const observer = new MutationObserver(updateTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, [mounted]);

  // Wait for theme to be mounted to avoid hydration mismatch
  // Use document theme (based on actual class) instead of resolvedTheme
  const sonnerTheme = mounted ? documentTheme : 'light';

  // Render Toaster in a portal outside #root to ensure it's in the same
  // stacking context as Radix Dialog modals
  // Wrap in Theme component to preserve theme context and CSS variables
  return createPortal(
    <Theme accentColor="indigo" grayColor="slate" panelBackground="solid">
      <Sonner
        theme={sonnerTheme}
        position="bottom-center"
        richColors={true}
        closeButton
        expand={false}
        offset="1rem"
        className="toaster"
        {...props}
      />
    </Theme>,
    document.body
  );
};

export { Toaster };
